import {
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
  RowNode,
  RowSelectedEvent,
  SortChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { Observable } from 'rxjs';

export type NbGridRowType = 'clientSide' | 'serverSide' | 'infinite';

export enum NbGridSortType {
  ASC = 'asc',
  DESC = 'desc',
}

export interface INbGridColumnDef {
  field: string;
  headerName?: string;
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  cellClass?: string | string[];
  cellStyle?: { [styleName: string]: string };
  cellFormat?: (param: ValueFormatterParams) => string;
  cellTemplate?: (param: ICellRendererParams) => string | HTMLElement;
  enableColumnMenu?: boolean;

  // other options
  [key: string]: any;
}

export interface IHeaderBarButtonGroup {
  template: string;
  isDisplay: () => boolean;
  action: () => void;
}

export interface IHeaderBarDropDownOption {
  template: string;
  isDisplay: () => boolean;
  action: () => void;
}

export interface IHeaderBarOptions {
  enableSearchBar: boolean;
  searchIcon: string;
  searchCloseIcon: string;
  searchPlaceholder: string;
  buttonGroupOnLeft: IHeaderBarButtonGroup[];
  buttonGroupOnRight: IHeaderBarButtonGroup[];
  enableDropDownMenu: boolean;
  dropDownMenu: IHeaderBarDropDownOption[];
}

export interface IGetMoreRowsParams {
  totalPages: number;
  lastPage: number;
  startRow: number;
  endRow: number;
  sortBy?: string;
  sortType?: 'asc' | 'desc';
}

export interface IGetMoreRowsResponse<T = any> {
  totalPages: number;
  lastPage: number;
  delta: T[];
}

export type IGetContextMenuItemsFunction = (param: GetContextMenuItemsParams) => MenuItemDef[];

export interface INbGridOptions<T = any> {
  // selection options
  enableFullRowSelection?: boolean;
  enableRowHeaderSelection?: boolean;
  enableRowCheckboxSelection?: boolean;
  enableMultiSelect?: boolean;
  onRowSelected?: (e: RowSelectedEvent) => void;
  isRowSelectable?: (rowNode: RowNode) => boolean;

  // column options
  enableColumnResize?: boolean;
  columnDefs: INbGridColumnDef[];

  // header bar options
  enableHeaderBar?: boolean;
  headerBarDef?: IHeaderBarOptions;

  // row context menu options
  enableRowContextMenu?: boolean;
  rowContextMenuItems?: MenuItemDef[] | IGetContextMenuItemsFunction;

  // sorting & filtering options
  enableSorting?: boolean;
  onSortChanged?: (e: SortChangedEvent) => void;
  onFilterChanged?: (a: FilterChangedEvent) => void;
  isExternalFilterPresent?: () => boolean;
  doesExternalFilterPass?: (rowNode: RowNode) => boolean;

  // grid options
  immutableData?: boolean;
  onGridReady: (gridApi: GridReadyEvent) => void;
  onGridDestroy?: () => void;
  getRowNodeId: (rowData: T) => string;

  // animation options
  enableColumnAnimation?: boolean;
  enableRowAnimation?: boolean;

  // lazy loading
  enableLazyLoading?: boolean;
  getMoreRows?: (params: IGetMoreRowsParams) => Observable<IGetMoreRowsResponse<T>>;

  // misc
  enableCellTextSelection?: boolean;
  enableBrowserTooltips?: boolean;

  // other options
  [key: string]: any;
}

export const defaultOptions: INbGridOptions = {
  enableFullRowSelection: true,
  enableRowHeaderSelection: false,
  enableRowCheckboxSelection: false,
  enableMultiSelect: true,
  onRowSelected: () => {},
  isRowSelectable: () => true,

  enableColumnResize: true,
  columnDefs: [],

  enableRowContextMenu: false,
  rowContextMenuItems: () => [],

  enableSorting: true,
  enableFilter: false,
  onSortChanged: () => {},
  onFilterChanged: () => {},
  isExternalFilterPresent: () => false,
  doesExternalFilterPass: () => true,

  enableColumnAnimation: false,
  enableRowAnimation: false,

  immutableData: true,
  onGridReady: () => {},
  getRowNodeId: data => data.id,

  enableLazyLoading: false,
  getMoreRows: null,

  enableCellTextSelection: true,
  enableBrowserTooltips: true,
};

// use font awesome icons instead of ag-grid built-in icons
export const icons = {
  // column header items
  menu: '<i class="fas fa-bars"></i>',
  //   filter
  //   columns
  sortAscending: '<i class="fas fa-caret-up"></i>',
  sortDescending: '<i class="fas fa-caret-down"></i>',
  //   sortUnSort
  //
  // // expand / contract row group
  //   groupExpanded
  //   groupContracted
  //
  // // expand / contract column group
  //   columnGroupOpened
  //   columnGroupClosed
  //
  // // tool panel column group open / close
  //   columnSelectOpen
  //   columnSelectClosed
  //
  // // row checkbox selection and tool panel column selection
  //   checkboxChecked
  //   checkboxUnchecked
  //   checkboxIndeterminate
  //
  // // tool panel column selection, when read only (ie disabled checkboxes)
  //   checkboxCheckedReadOnly
  //   checkboxUncheckedReadOnly
  //   checkboxIndeterminateReadOnly
  //
  // // when moving columns
  //   columnMovePin // when column is to the left, before it gets pinned
  //   columnMoveAdd // when adding a column
  //   columnMoveHide // when removing a column
  //   columnMoveMove // when moving a column
  //   columnMoveLeft // when moving and scrolling left
  //   columnMoveRight // when moving and scrolling right
  //   columnMoveGroup // when about to drop into group panel
  //   columnMoveValue // when about to drop into value panel
  //   columnMovePivot // when about to drop into pivot panel
  //   dropNotAllowed // when trying to drop column into group/value/pivot panel and column doesn't support it
  //
  // // menu
  //   menuPin // beside the column pin menu item
  //   menuValue // beside the column value menu item
  //   menuAddRowGroup // beside the column row group menu item
  //   menuRemoveRowGroup // beside the column row group menu item
  //   clipboardCopy // beside the copy to clipboard menu item
  //   clipboardPaste // beside the paste from clipboard menu item
  //
  // // column drop panels
  //   rowGroupPanel // beside where to drop columns for row group
  //   pivotPanel // beside where to drop columns for pivot
  //   valuePanel // beside where to drop columns for value
};
